import { useMemo } from "react";
import countries from "@/assets/data/countries.json";

// Types for location data
export interface State {
  code: string;
  name: string;
  cities: City[];
}

export interface City {
  code: string;
  name: string;
}

export interface Country {
  name: string;
  states: State[];
}

/**
 * Hook to get available states for a given country
 */
export function useAvailableStates(selectedCountry: string) {
  return useMemo(() => {
    if (!selectedCountry) return [];
    const country = countries.find(
      (country) => country.name === selectedCountry
    );
    return country?.states || [];
  }, [selectedCountry]);
}

/**
 * Hook to get available cities for a given country and state
 */
export function useAvailableCities(
  selectedCountry: string,
  selectedState: string | undefined
) {
  return useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const country = countries.find(
      (country) => country.name === selectedCountry
    );
    const state = country?.states.find((state) => state.code === selectedState);
    return state?.cities || [];
  }, [selectedCountry, selectedState]);
}

/**
 * Get all countries
 */
export function getAllCountries() {
  return countries;
}

/**
 * Get states for a specific country (non-hook version)
 */
export function getStatesForCountry(countryName: string): State[] {
  if (!countryName) return [];
  const country = countries.find((country) => country.name === countryName);
  return country?.states || [];
}

/**
 * Get cities for a specific country and state (non-hook version)
 */
export function getCitiesForState(
  countryName: string,
  stateCode: string
): City[] {
  if (!countryName || !stateCode) return [];
  const country = countries.find((country) => country.name === countryName);
  const state = country?.states.find((state) => state.code === stateCode);
  return state?.cities || [];
}

export interface MapLocationAddressParts {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  address?: string;
}

export interface RegistrationAddressFields {
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressCountry: string;
}

/**
 * Map a geocoded pin onto registration address fields, matching country/state/city
 * lookups when possible so the existing selects can display the values.
 */
export function resolveRegistrationAddress(
  parts: MapLocationAddressParts,
  current: RegistrationAddressFields
): RegistrationAddressFields {
  const addressStreet = (
    parts.street ||
    parts.address ||
    current.addressStreet
  ).trim();
  const addressPostalCode =
    parts.postalCode?.trim() || current.addressPostalCode;

  const countryQuery = parts.country?.trim().toLowerCase() ?? "";
  const countryMatch = countryQuery
    ? getAllCountries().find(
      (country) => country.name.toLowerCase() === countryQuery
    )
    : undefined;
  const addressCountry = countryMatch?.name || current.addressCountry;

  const states = addressCountry ? getStatesForCountry(addressCountry) : [];
  const stateQuery = parts.state?.trim().toLowerCase() ?? "";
  const stateMatch = stateQuery
    ? states.find(
      (state) =>
        state.code.toLowerCase() === stateQuery ||
        state.name.toLowerCase() === stateQuery
    )
    : undefined;

  const addressState = countryMatch
    ? (stateMatch?.code ?? "")
    : stateMatch?.code || current.addressState;

  const cities =
    addressCountry && addressState
      ? getCitiesForState(addressCountry, addressState)
      : [];
  const cityQuery = parts.city?.trim().toLowerCase() ?? "";
  const cityMatch = cityQuery
    ? cities.find((city) => city.name.toLowerCase() === cityQuery)
    : undefined;

  const addressCity =
    countryMatch || stateMatch
      ? (cityMatch?.name ?? "")
      : cityMatch?.name || current.addressCity;

  return {
    addressStreet,
    addressPostalCode,
    addressCountry,
    addressState,
    addressCity
  };
}
